import {
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
} from "@jhool-io/fe-components";
import { useFetchInsuranceProviders } from "../../modules/clients/hooks/clients.queries";
import useShowInsuranceOverSelfPay from "../../modules/practice-settings/hooks/useShowInsuranceOverSelfPay";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { removeEnumUnderscore, truncateString } from "../../utils/helpers";

const SELF_PAY_INSURANCE_PROVIDER_ID = "self_pay";

const SELECT_ALL_VALUE = "select_all";

const INSURANCE_PROVIDER_IDS_PARAM = "insurance_provider_ids[]";

const LEGACY_INSURANCE_PROVIDER_PARAM = "insurance_provider";

export function getInsuranceProviderIdsFilter(
    searchParams: URLSearchParams
): string[] {
    const insuranceProviderIds = searchParams.getAll(
        INSURANCE_PROVIDER_IDS_PARAM
    );
    const legacyInsuranceProviderId = searchParams.get(
        LEGACY_INSURANCE_PROVIDER_PARAM
    );
    return insuranceProviderIds.length === 0 && legacyInsuranceProviderId
        ? [legacyInsuranceProviderId]
        : insuranceProviderIds;
}

export function setInsuranceProviderIdsFilter(
    searchParams: URLSearchParams,
    insuranceProviderIds: string[]
) {
    searchParams.delete(LEGACY_INSURANCE_PROVIDER_PARAM);
    searchParams.delete(INSURANCE_PROVIDER_IDS_PARAM);
    insuranceProviderIds.forEach((insuranceProviderId) =>
        searchParams.append(INSURANCE_PROVIDER_IDS_PARAM, insuranceProviderId)
    );
}

interface InsuranceProviderFilterProps {
    /** Selected insurance provider ids */
    values: string[];
    /** Called with the full list of selected insurance provider ids */
    onChange: (values: string[]) => void;
    /** Show a self pay option unless the practice prioritizes insurance over self pay, defaults to true */
    showSelfPay?: boolean;
    /** Show a self pay option regardless of the practice setting */
    alwaysShowSelfPay?: boolean;
    /** Self pay can only be selected on its own */
    isSelfPayExclusive?: boolean;
    /** Alignment of the dropdown content, defaults to start on small screens and end otherwise */
    align?: "start" | "end";
    /** Disable the filter */
    disabled?: boolean;
}

export default function InsuranceProviderFilter({
    values,
    onChange,
    showSelfPay = true,
    alwaysShowSelfPay,
    isSelfPayExclusive,
    align,
    disabled,
}: InsuranceProviderFilterProps) {
    const insuranceProviders = useFetchInsuranceProviders();
    const showInsOverSelfPay = useShowInsuranceOverSelfPay();
    const isSmallScreen = useMediaQuery("(max-width: 768px)");

    const isSelfPayShown =
        alwaysShowSelfPay || (showSelfPay && !showInsOverSelfPay);

    const insuranceOptions = (insuranceProviders.data?.data || []).map(
        (provider) => ({
            label: removeEnumUnderscore(provider.alias || provider.name),
            value: provider.insurance_provider_id,
        })
    );
    const options = (
        isSelfPayShown
            ? [
                  { label: "Self pay", value: SELF_PAY_INSURANCE_PROVIDER_ID },
                  ...insuranceOptions,
              ]
            : insuranceOptions
    ).sort((a, b) => a.label.localeCompare(b.label));

    const selectAllValues = (
        isSelfPayExclusive ? insuranceOptions : options
    ).map((option) => option.value);
    const areAllSelected =
        selectAllValues.length > 0 &&
        selectAllValues.every((value) => values.includes(value));

    const getTriggerText = () => {
        if (insuranceProviders.isLoading) return "Loading..";
        if (insuranceProviders.error) return "Error loading providers";
        if (values.length > 1) return `Insurance (${values.length})`;
        const selectedOption = options.find(
            (option) => option.value === values[0]
        );
        return selectedOption
            ? truncateString(selectedOption.label)
            : "Insurance";
    };

    const handleSelectAllClick = () => {
        onChange(areAllSelected ? [] : selectAllValues);
    };

    const handleOptionClick = (value: string) => {
        if (values.includes(value)) {
            onChange(values.filter((selectedValue) => selectedValue !== value));
            return;
        }
        if (
            isSelfPayExclusive &&
            (value === SELF_PAY_INSURANCE_PROVIDER_ID ||
                values.includes(SELF_PAY_INSURANCE_PROVIDER_ID))
        ) {
            onChange([value]);
            return;
        }
        onChange([...values, value]);
    };

    return (
        <Dropdown>
            <DropdownTrigger
                asChild
                disabled={
                    disabled ||
                    Boolean(insuranceProviders.error) ||
                    insuranceProviders.isLoading
                }
            >
                <FilterButton>{getTriggerText()}</FilterButton>
            </DropdownTrigger>
            <DropdownContent
                align={align || (isSmallScreen ? "start" : "end")}
                width={isSmallScreen ? "250px" : "auto"}
                maxHeight={216}
            >
                <DropdownItem
                    onClick={handleSelectAllClick}
                    isMulti
                    itemValue={SELECT_ALL_VALUE}
                    values={areAllSelected ? [SELECT_ALL_VALUE] : []}
                >
                    Select all
                </DropdownItem>
                {options.map((option) => (
                    <DropdownItem
                        key={option.value}
                        onClick={() => handleOptionClick(option.value)}
                        isMulti
                        itemValue={option.value}
                        values={values}
                    >
                        {option.label}
                    </DropdownItem>
                ))}
            </DropdownContent>
        </Dropdown>
    );
}
